import { Icon, IconSource, TextButton } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { font } from '@creditornot/alchemy-react/tokens';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useUserLocale } from 'common/hooks/useUserLocale';
import { formatDateTime } from 'common/utils/dates/dateUtils';

import { BulkOperation } from '../api-types';
import { isFinalStatus } from './utils';

type IconAttributes = {
  icon: IconSource;
  color: string;
};

type StatusKey = BulkOperation['status'] | 'FINISHED_WITH_ERRORS';

const BULK_OPERATION_STATUS_ICON: Record<StatusKey, IconAttributes> = {
  QUEUED: { icon: icons.squareLayers, color: color.textSubdued },
  PROCESSING: { icon: icons.cloudArrowUp, color: color.textPositive },
  FINISHED: { icon: icons.checkmarkDouble, color: color.textSubdued },
  FINISHED_WITH_ERRORS: { icon: icons.checkmark, color: color.textSubdued },
  FAILED: { icon: icons.crossCircle, color: color.textNegative },
};

function getBulkOperationStatusIconAttributes(
  status: BulkOperation['status'],
  hasErrors: boolean,
): IconAttributes {
  const key: StatusKey = status === 'FINISHED' && hasErrors ? 'FINISHED_WITH_ERRORS' : status;
  return BULK_OPERATION_STATUS_ICON[key];
}

type Props = {
  operation: BulkOperation;
  downloadReportPending: boolean;
  onDownloadReport: (id: string) => void;
};

export function BulkOperationEntry({ operation, downloadReportPending, onDownloadReport }: Props) {
  const format = useFormatMessage();
  const locale = useUserLocale();

  const allowDownloadReport =
    isFinalStatus(operation.status) && (operation.errorCount > 0 || operation.warningCount > 0);

  const hasErrors = operation.errorCount > 0;
  const statusIcon = getBulkOperationStatusIconAttributes(operation.status, hasErrors);

  return (
    <Stack gap="narrowSmall" alignItems="flex-start">
      <div style={{ font: font.footnote, color: color.textSubdued }}>
        {formatDateTime(locale, operation.createdAt)}
      </div>
      <div style={{ font: font.caption, color: color.textSubdued }}>{operation.fileName || ''}</div>
      <Stack direction="row" alignItems="center" gap="regularLarge">
        <Stack direction="row" alignItems="center" gap="narrowSmall">
          <Icon source={statusIcon.icon} size="small" fill={statusIcon.color} />

          <div style={{ font: font.caption }}>
            {operation.processedRows ?? 0} / {operation.totalRows ?? 0}
          </div>
        </Stack>
        {(operation.warningCount ?? 0) > 0 && (
          <Stack direction="row" alignItems="center" gap="narrowSmall">
            <Icon source={icons.alert} size="small" fill={color.textWarning} />
            <div style={{ font: font.caption }}>{operation.warningCount ?? 0}</div>
          </Stack>
        )}
        {(operation.errorCount ?? 0) > 0 && (
          <Stack direction="row" alignItems="center" gap="narrowSmall">
            <Icon source={icons.crossCircle} size="small" fill={color.textNegative} />
            <div style={{ font: font.caption }}>{operation.errorCount ?? 0}</div>
          </Stack>
        )}
      </Stack>
      {allowDownloadReport && (
        <TextButton
          size="small"
          icon={icons.download}
          onClick={() => onDownloadReport(operation.id)}
          aria-label={format('forecasting.download_error_report')}
          disabled={downloadReportPending}
        >
          {format('forecasting.error_report')}
        </TextButton>
      )}
    </Stack>
  );
}
